import type { Difficulty } from '../../data/settings';

/** Status of a vertical-snake run. */
export enum RunStatus {
  RUNNING = 'RUNNING',
  PAUSED = 'PAUSED',
  GAME_OVER = 'GAME_OVER',
}

export enum Biome {
  MEADOW = 'MEADOW',
  REEF = 'REEF',
  LATTICE = 'LATTICE',
  MAELSTROM = 'MAELSTROM',
}

export const BIOME_ORDER: readonly Biome[] = [
  Biome.MEADOW,
  Biome.REEF,
  Biome.LATTICE,
  Biome.MAELSTROM,
];

export const BIOME_DISPLAY: Record<Biome, string> = {
  [Biome.MEADOW]: 'Meadow',
  [Biome.REEF]: 'Reef',
  [Biome.LATTICE]: 'Lattice',
  [Biome.MAELSTROM]: 'Maelstrom',
};

export const BIOME_TAGLINE: Record<Biome, string> = {
  [Biome.MEADOW]: 'Learn the lanes',
  [Biome.REEF]: 'Drift through kelp',
  [Biome.LATTICE]: 'Pick a portal',
  [Biome.MAELSTROM]: 'Survive the storm',
};

export function biomeForLevel(level: number): Biome {
  const cyclePosition = (level - 1) % 12;
  if (cyclePosition >= 9) return Biome.MAELSTROM;
  if (cyclePosition >= 6) return Biome.LATTICE;
  if (cyclePosition >= 3) return Biome.REEF;
  return Biome.MEADOW;
}

export const BIOME_FIRST_LEVEL: Record<Biome, number> = {
  [Biome.MEADOW]: 1,
  [Biome.REEF]: 4,
  [Biome.LATTICE]: 8,
  [Biome.MAELSTROM]: 13,
};

export enum SteerDir {
  NONE = 'NONE',
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

export const STEER_SIGN: Record<SteerDir, number> = {
  [SteerDir.NONE]: 0,
  [SteerDir.LEFT]: -1,
  [SteerDir.RIGHT]: 1,
};

export enum FoodKind {
  NORMAL = 'NORMAL',
  GOLDEN = 'GOLDEN',
}

export interface ObstacleBase {
  x: number;
  y: number;
  radius: number;
  damage: number;
  seed: number;
}

export type Obstacle =
  | ObstacleGate
  | ObstaclePulseWall
  | ObstacleDoubleGate
  | ObstacleLog
  | ObstacleBeehive
  | ObstacleKelpCurtain
  | ObstacleJellyfish
  | ObstacleCoralSpire
  | ObstaclePortalWall
  | ObstacleDiagonalBeam
  | ObstacleCrossGate
  | ObstacleShiftingWall
  | ObstacleChaosRing
  | ObstacleComet;

export const DEFAULT_DAMAGE = 1;

export interface ObstacleGate extends ObstacleBase {
  type: 'GATE';
  gapCenterX: number;
  gapWidth: number;
  pillarHalfWidth: number;
  pillarHeight: number;
}

export interface ObstaclePulseWall extends ObstacleBase {
  type: 'PULSE_WALL';
  totalWidth: number;
  gapStart: number;
  gapWidth: number;
}

export interface ObstacleDoubleGate extends ObstacleBase {
  type: 'DOUBLE_GATE';
  gapCenterX: number;
  gapWidth: number;
  spacing: number;
}

export interface ObstacleLog extends ObstacleBase {
  type: 'LOG';
  width: number;
  height: number;
  rotation: number;
}

export interface ObstacleBeehive extends ObstacleBase {
  type: 'BEEHIVE';
  ringRadius: number;
  beeCount: number;
  phase: number;
  rotationSpeed: number;
}

export interface ObstacleKelpCurtain extends ObstacleBase {
  type: 'KELP_CURTAIN';
  totalWidth: number;
  slits: ReadonlyArray<readonly [number, number]>;
}

export interface ObstacleJellyfish extends ObstacleBase {
  type: 'JELLYFISH';
  driftPhase: number;
  driftAmplitude: number;
  driftSpeed: number;
}

export interface ObstacleCoralSpire extends ObstacleBase {
  type: 'CORAL_SPIRE';
  halfWidth: number;
  height: number;
}

export interface ObstaclePortalWall extends ObstacleBase {
  type: 'PORTAL_WALL';
  totalWidth: number;
  gaps: ReadonlyArray<readonly [number, number]>;
}

export interface ObstacleDiagonalBeam extends ObstacleBase {
  type: 'DIAGONAL_BEAM';
  length: number;
  thickness: number;
  angle: number;
}

export interface ObstacleCrossGate extends ObstacleBase {
  type: 'CROSS_GATE';
  armLength: number;
  armThickness: number;
  phase: number;
  rotationSpeed: number;
}

export interface ObstacleShiftingWall extends ObstacleBase {
  type: 'SHIFTING_WALL';
  totalWidth: number;
  gapWidth: number;
  phase: number;
  amplitude: number;
  speed: number;
}

export interface ObstacleChaosRing extends ObstacleBase {
  type: 'CHAOS_RING';
  totalWidth: number;
  innerGapWidth: number;
  outerGapWidth: number;
  spacing: number;
  innerPhase: number;
  outerPhase: number;
  slideSpeed: number;
}

export interface ObstacleComet extends ObstacleBase {
  type: 'COMET';
  velocityX: number;
  radiusBody: number;
}

export interface Food {
  x: number;
  y: number;
  seed: number;
  kind: FoodKind;
}

export interface Popup {
  x: number;
  y: number;
  text: string;
  life: number;
  maxLife: number;
  color: number;
}

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: number;
  radius: number;
}

export interface World {
  width: number;
  scrollY: number;
  scrollSpeed: number;
  level: number;
  levelProgress: number;
  timeToNextLevel: number;
  snakeLength: number;
  peakSnakeLength: number;
  snakeBody: ReadonlyArray<readonly [number, number]>;
  snakeVx: number;
  snakeMaxSpeed: number;
  food: ReadonlyArray<Food>;
  obstacles: ReadonlyArray<Obstacle>;
  particles: ReadonlyArray<Particle>;
  popups: ReadonlyArray<Popup>;
  score: number;
  highScore: number;
  longestRun: number;
  furthestLevel: number;
  status: RunStatus;
  shakeSeconds: number;
  shakeMagnitude: number;
  biome: Biome;
  biomeMessage: string | null;
  biomeMessageSeconds: number;
  rushSeconds: number;
  calmSeconds: number;
  runSeed: number;
  levelUpCounter: number;
  slowMoSeconds: number;
  difficulty: Difficulty;
  headFrac: number;
  combo: number;
  invincibilitySeconds: number;
  milestoneIndex: number;
}

export type GameEvent =
  | { type: 'ATE_FOOD'; kind: FoodKind; combo: number; scoreGain: number }
  | { type: 'TURNED' }
  | { type: 'GAME_OVER'; score: number; newHighScore: boolean; snakeLength: number }
  | { type: 'SCORE_MILESTONE'; score: number }
  | { type: 'LEVEL_UP'; newLevel: number; biome: Biome; newScrollSpeed: number };
